import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict, format } from "date-fns";
import clsx from "clsx";
import queryString from "query-string";

import { QueryModel, FileModel, AttachmentModel } from "@interfaces/index";
import { useQueryGetOne, useQueryReply } from "@providers/index";
import { useNotify } from "@hooks/index";
import { attachmentsService } from "@services/index";
import {
  AlertTriangleSvg,
  InfoSquareRoundedSvg,
  PhoneSvg,
  Loader,
  QueryMessage,
  Button,
  FormText,
  FormFile,
  FileItem,
  FilePreview,
} from "@components/index";

export const QueryItem = ({
  query,
  isOpen,
  onToggle,
}: {
  query: QueryModel;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const notify = useNotify();
  const location = useLocation();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState<FileModel[]>([]);
  const [isUploadingReplyFiles, setIsUploadingReplyFiles] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState<AttachmentModel>();

  const {
    data: dataGetOne,
    isPending: isPendingGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
    refetch: refetchGetOne,
  } = useQueryGetOne(isOpen ? query.uniqueId : "");

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  const { mutate: mutateQueryReply, isPending: isPendingQueryReply } =
    useQueryReply(
      () => {
        setReplyText("");
        setReplyFiles([]);
        notify("success", "Your reply has been sent.");
        refetchGetOne();
        const params = queryString.parse(location.search);
        navigate(
          `?${queryString.stringify(
            { ...params, activeSubtab: "awaiting_fbtc" },
            { skipEmptyString: true },
          )}`,
        );
      },
      (error) => notify("error", error.message),
    );

  const handleSelectReplyFiles = async (target: HTMLInputElement) => {
    setIsUploadingReplyFiles(true);
    if (!target.files?.length) {
      setIsUploadingReplyFiles(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    setIsUploadingReplyFiles(false);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));
    setReplyFiles((prev) => [...prev, ...newFilesWithSizes]);
  };

  const handleRemoveReplyFile = (uuid: string) => {
    setReplyFiles((prev) => prev.filter((file) => file.uuid !== uuid));
  };

  const handleSubmitReply = () => {
    mutateQueryReply({
      queryId: query.uniqueId,
      message: replyText.trim(),
      files: replyFiles,
    });
  };

  return (
    <div
      className={clsx(
        "rounded-lg border transition-colors",
        isOpen ? "border-primary-100" : "border-lightGray",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          "w-full rounded-lg border px-4 py-3 text-left transition-colors duration-150",
          isOpen
            ? "border-primary-100 bg-primary-150"
            : "border-lightGray bg-white-100 hover:border-primary-104 hover:bg-primary-150",
        )}
      >
        <div className="flex items-start justify-between gap-x-2">
          <span className="truncate text-300 font-bold text-black-100">
            {query.subject}
          </span>
          {query.lastMessage && (
            <div className="flex shrink-0 items-center gap-x-1.5">
              <span className="whitespace-nowrap text-100 text-black-54">
                {formatDistanceToNowStrict(new Date(query.lastMessage.sentAt), {
                  addSuffix: true,
                })}
              </span>
            </div>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <div
            className={clsx(
              "flex w-fit items-center gap-x-1 rounded-2xl px-2.5 py-1 text-90 *:size-[12px] *:shrink-0",
              query.type.value === "action_required" && "bg-red-8 text-red-100",
              query.type.value === "clarification" &&
                "bg-black-4 text-black-54",
            )}
          >
            {query.type.value === "action_required" && <AlertTriangleSvg />}
            {query.type.value === "clarification" && <InfoSquareRoundedSvg />}
            {query.type.label}
          </div>
          {query.callRequested && (
            <span className="font-medium flex items-center gap-x-1 rounded-full bg-primary-108 px-2 py-0.5 text-90 text-primary-300 *:size-3">
              <PhoneSvg />
              Call requested
            </span>
          )}
          <span className="text-90 text-black-32">
            Raised {format(new Date(query.createdAt), "d MMM yyyy")}
          </span>
        </div>
        {query.lastMessage && (
          <p className="mt-2 truncate text-200 text-black-54">
            <span className="font-bold">
              {query.lastMessage.sender === "fbtc" ? "FBTC" : "You"}:{" "}
            </span>
            {query.lastMessage.body}
          </p>
        )}
      </button>
      {isOpen && (
        <div className="border-t border-lightGray">
          {isPendingGetOne && (
            <div className="relative h-40">
              <Loader />
            </div>
          )}
          {!isPendingGetOne && dataGetOne && (
            <>
              {dataGetOne.callRequested && (
                <div className="border-b border-lightGray px-6 py-2">
                  <span className="font-medium w-fitgap-x-1 flex items-center rounded-full bg-primary-108 px-2 py-0.5 text-90 text-primary-300 *:size-3">
                    <PhoneSvg />
                    Call requested
                  </span>
                </div>
              )}
              <div className="flex max-h-[420px] flex-col gap-y-4 overflow-y-auto px-6 py-4">
                {dataGetOne.messages.length > 0 ? (
                  dataGetOne.messages.map((message) => (
                    <QueryMessage
                      key={message.id}
                      message={message}
                      onPreviewAttachment={setAttachmentPreview}
                    />
                  ))
                ) : (
                  <p className="py-6 text-center text-200 text-black-32">
                    No messages
                  </p>
                )}
              </div>
              <FilePreview
                attachment={attachmentPreview}
                onClose={() => setAttachmentPreview(undefined)}
              />
              <div className="border-t border-lightGray px-6 py-4">
                {dataGetOne.status.value === "awaiting_client" && (
                  <div className="flex flex-col gap-y-2">
                    <FormText
                      name="replyText"
                      label=""
                      value={replyText}
                      onChange={({ currentTarget }) =>
                        setReplyText(currentTarget.value)
                      }
                      placeholder="Write your response…"
                      rows={3}
                      className="min-h-0 resize-none"
                      disabled={isPendingQueryReply}
                    />
                    <FormFile
                      name="replyFiles"
                      label="Attach files"
                      dataTestid="input_queryReplyFiles"
                      uploading={isUploadingReplyFiles}
                      disabled={isPendingQueryReply}
                      onChange={(event) => {
                        handleSelectReplyFiles(
                          event.target as HTMLInputElement,
                        );
                        event.preventDefault();
                      }}
                    />
                    {!!replyFiles.length && (
                      <div className="flex flex-col gap-y-2">
                        {replyFiles.map((file) => (
                          <FileItem
                            key={file.uuid}
                            file={file}
                            onRemove={handleRemoveReplyFile}
                          />
                        ))}
                      </div>
                    )}
                    <Button
                      title="Submit"
                      height="xsmall"
                      disabled={
                        (!replyText.trim() && !replyFiles.length) ||
                        isPendingQueryReply ||
                        isUploadingReplyFiles
                      }
                      onClick={handleSubmitReply}
                      className="self-end"
                    />
                  </div>
                )}
                {dataGetOne.status.value === "awaiting_fbtc" && (
                  <p className="text-center text-200 text-black-32">
                    Submitted — FBTC is reviewing your response.
                  </p>
                )}
                {dataGetOne.status.value === "resolved" && (
                  <p className="text-center text-200 text-black-32">
                    This query has been resolved.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
